import { atom, selector, useRecoilState, useSetRecoilState } from "recoil";
import { useCallback } from "react";
import { savedDisplayOperationState, useDisplayOperationRecoilState } from "../hooks/useDisplayOperation";
import { getProperty } from "../utils/FPEUtils";

const SCREEN_SETTING = 'SCREEN_SETTING';

/**
 * 地図言語
 */
export const googleMapLanguageState = atom({
  key: 'googleMapLanguageState',
  default: null
});

/**
 * 地図スタイル(地図カラーテーマ + 各種表示設定）
 */
export const googleMapStyleState = atom({
  key: 'googleMapStyleState',
  default : selector({
    key: 'googleMapStyleStateSelector',
    get: ({get}) => {
      const road = get(roadVisibleState);
      const roadLabel = get(roadLabelVisibleState);
      const landmark = get(landmarkVisibleState);
      const label = get(labelVisibleState);
      const theme = getTheme(get(googleMapThemeNameState));
      const style = makeStyle(road, roadLabel, landmark, label, theme);
      return style;
    }
  })
});

/**
 * 地図カラーテーマ名
 * @type {{DARK3: string, DARK2: string, WHITE: string, DARK: string, STANDARD: string, SATELLITE: string}}
 */
export const GOOGLE_MAP_THEME = {
  STANDARD: 'Standard',
  WHITE: 'White',
  DARK: 'Dark',
  DARK2: 'Dark2',
  DARK3: 'Dark3',
  SATELLITE: 'Satellite'
}

/**
 * 選択中の地図カラーテーマ名
 */
export const googleMapThemeNameState = atom({
  key: 'googleMapThemeNameState',
  default: selector({
    key: 'googleMapThemeNameStateSelector',
    get: ({get}) => {
      const val = getProperty(get(savedDisplayOperationState), 'SCREEN_SETTING.google.theme');
      return val !== undefined ? val : GOOGLE_MAP_THEME.STANDARD;
    }
  })
});

/**
 * 選択中の地図カラーテーマ
 */
const googleMapThemeState = atom({
  key: 'googleMapThemeState',
  default: selector({
    key: 'googleMapThemeStateSelector',
    get: ({get}) => getTheme(get(googleMapThemeNameState))
  })
});

/**
 * 道路表示状態
 */
const roadVisibleState = atom({
  key: 'roadVisibleState',
  default: selector({
    key: 'roadVisibleStateSelector',
    get: ({get}) => {
      const val = getProperty(get(savedDisplayOperationState), 'SCREEN_SETTING.google.road');
      return val !== undefined ? val : true;
    }
  })
});

/**
 * 道路（ラベル）表示状態
 */
const roadLabelVisibleState = atom({
  key: 'roadLabelVisibleState',
  default: selector({
    key: 'roadLabelVisibleStateSelector',
    get: ({get}) => {
      const val = getProperty(get(savedDisplayOperationState), 'SCREEN_SETTING.google.roadlabel');
      return val !== undefined ? val : true;
    }
  })
});

/**
 * ランドマーク表示状態
 */
const landmarkVisibleState = atom({
  key: 'landmarkVisibleState',
  default: selector({
    key: 'landmarkVisibleStateSelector',
    get: ({get}) => {
      const val = getProperty(get(savedDisplayOperationState), 'SCREEN_SETTING.google.landmark');
      return val !== undefined ? val : true;
    }
  })
});

/**
 * ラベル表示状態
 */
const labelVisibleState = atom({
  key: 'labelVisibleState',
  default: selector({
    key: 'labelVisibleStateSelector',
    get: ({get}) => {
      const val = getProperty(get(savedDisplayOperationState), 'SCREEN_SETTING.google.label');
      return val !== undefined ? val : true;
    }
  })
});

const makeStyle = (road, roadLabel, landmark, label, theme) => {
  let style = theme
  if (!road) {
    style = style.concat([{
      "featureType": "road",
      "stylers": [{"visibility": "off"}]
    },{
      "featureType": "transit",
      "stylers": [{"visibility": "off"}]
    }]);
  }
  if (!roadLabel) {
    style = style.concat([  {
      "featureType": "road",
      "elementType": "labels",
      "stylers": [{"visibility": "off"}]
    },{
      "featureType": "transit",
      "elementType": "labels",
      "stylers": [{"visibility": "off"}]
    }]);
  }
  if (!landmark) {
    style = style.concat([
      {
        "featureType": "administrative",
        "elementType": "geometry",
        "stylers": [{"visibility": "off"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels",
        "stylers": [{"visibility": "off"}]
      },
      {
        "featureType": "landscape.man_made",
        "elementType": "labels",
        "stylers": [{"visibility": "off"}]
      }
    ]);
  }
  if (!label) {
    style = style.concat([
      {
        "featureType": "administrative",
        "stylers": [{"visibility": "off"}]
      },
      {
        "featureType": "water",
        "elementType": "labels",
        "stylers": [{"visibility": "off"}]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "labels",
        "stylers": [{"visibility": "off"}]
      }
    ])
  }
  return style;
};

const whiteTheme = [
  {
    "elementType": "geometry",
    "stylers": [{"color": "#f5f5f5"}]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#616161"}]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#f5f5f5"}]
  },
  {
    "featureType": "administrative.land_parcel",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#bdbdbd"}]
  },
  {
    "featureType": "poi",
    "elementType": "geometry",
    "stylers": [{"color": "#eeeeee"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#757575"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [{"color": "#e5e5e5"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#9e9e9e"}]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [{"color": "#ffffff"}]
  },
  {
    "featureType": "road.arterial",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#757575"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{"color": "#dadada"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#616161"}]
  },
  {
    "featureType": "road.local",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#9e9e9e"}]
  },
  {
    "featureType": "transit.line",
    "elementType": "geometry",
    "stylers": [{"color": "#e5e5e5"}]
  },
  {
    "featureType": "transit.station",
    "elementType": "geometry",
    "stylers": [{"color": "#eeeeee"}]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{"color": "#c9c9c9"}]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#9e9e9e"}]
  }
];

const darkTheme = [
  {
    "elementType": "geometry",
    "stylers": [{"color": "#212121"}]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#757575"}]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#212121"}]
  },
  {
    "featureType": "administrative",
    "elementType": "geometry",
    "stylers": [{"color": "#757575"}]
  },
  {
    "featureType": "administrative.country",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#9e9e9e"}]
  },
  {
    "featureType": "administrative.land_parcel",
    "stylers": [{"visibility": "off"}]
  },
  {
    "featureType": "administrative.locality",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#bdbdbd"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#757575"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [{"color": "#181818"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#616161"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#1b1b1b"}]
  },
  {
    "featureType": "road",
    "elementType": "geometry.fill",
    "stylers": [{"color": "#2c2c2c"}]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#8a8a8a"}]
  },
  {
    "featureType": "road.arterial",
    "elementType": "geometry",
    "stylers": [{"color": "#373737"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{"color": "#3c3c3c"}]
  },
  {
    "featureType": "road.highway.controlled_access",
    "elementType": "geometry",
    "stylers": [{"color": "#4e4e4e"}]
  },
  {
    "featureType": "road.local",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#616161"}]
  },
  {
    "featureType": "transit",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#757575"}]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{"color": "#000000"}]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#3d3d3d"}]
  }
]

const darkTheme2 = [
  {
    "elementType": "geometry",
    "stylers": [{"color": "#242f3e"}]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#746855"}]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#242f3e"}]
  },
  {
    "featureType": "administrative.locality",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#d59563"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#d59563"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [{"color": "#263c3f"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#6b9a76"}]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [{"color": "#38414e"}]
  },
  {
    "featureType": "road",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#212a37"}]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#9ca5b3"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{"color": "#746855"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#1f2835"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#f3d19c"}]
  },
  {
    "featureType": "transit",
    "elementType": "geometry",
    "stylers": [{"color": "#2f3948"}]
  },
  {
    "featureType": "transit.station",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#d59563"}]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{"color": "#17263c"}]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#515c6d"}]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#17263c"}]
  }
];

const darkTheme3 = [
  {
    "elementType": "geometry",
    "stylers": [{"color": "#1d2c4d"}]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#8ec3b9"}]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#1a3646"}]
  },
  {
    "featureType": "administrative.country",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#4b6878"}]
  },
  {
    "featureType": "administrative.land_parcel",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#64779e"}]
  },
  {
    "featureType": "administrative.province",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#4b6878"}]
  },
  {
    "featureType": "landscape.man_made",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#334e87"}]
  },
  {
    "featureType": "landscape.natural",
    "elementType": "geometry",
    "stylers": [{"color": "#023e58"}]
  },
  {
    "featureType": "poi",
    "elementType": "geometry",
    "stylers": [{"color": "#283d6a"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#6f9ba5"}]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#1d2c4d"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry.fill",
    "stylers": [{"color": "#023e58"}]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#3C7680"}]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [{"color": "#304a7d"}]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#98a5be"}]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#1d2c4d"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{"color": "#2c6675"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [{"color": "#255763"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#b0d5ce"}]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#023e58"}]
  },
  {
    "featureType": "transit",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#98a5be"}]
  },
  {
    "featureType": "transit",
    "elementType": "labels.text.stroke",
    "stylers": [{"color": "#1d2c4d"}]
  },
  {
    "featureType": "transit.line",
    "elementType": "geometry.fill",
    "stylers": [{"color": "#283d6a"}]
  },
  {
    "featureType": "transit.station",
    "elementType": "geometry",
    "stylers": [{"color": "#3a4762"}]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{"color": "#0e1626"}]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{"color": "#4e6d70"}]
  }
];

const getTheme = (theme) => {
  let _theme;
  switch (theme) {
    case GOOGLE_MAP_THEME.STANDARD:
      _theme = [];
      break;
    case GOOGLE_MAP_THEME.WHITE:
      _theme = whiteTheme;
      break;
    case GOOGLE_MAP_THEME.DARK:
      _theme = darkTheme;
      break;
    case GOOGLE_MAP_THEME.DARK2:
      _theme = darkTheme2;
      break;
    case GOOGLE_MAP_THEME.DARK3:
      _theme = darkTheme3;
      break;
    case GOOGLE_MAP_THEME.SATELLITE:
      _theme = [];
      break;
    default:
      _theme = [];
  }
  return _theme;
}

export const useGoogleMap = () => {
  const [roadVisible, setRoadVisible] = useDisplayOperationRecoilState(`${SCREEN_SETTING}.google.road`, roadVisibleState);
  const [roadLabelVisible, setRoadLabelVisible] = useDisplayOperationRecoilState(`${SCREEN_SETTING}.google.roadlabel`, roadLabelVisibleState);
  const [landmarkVisible, setLandmarkVisible] = useDisplayOperationRecoilState(`${SCREEN_SETTING}.google.landmark`, landmarkVisibleState);
  const [labelVisible, setLabelVisible] = useDisplayOperationRecoilState(`${SCREEN_SETTING}.google.label`, labelVisibleState);
  const setGoogleMapStyleState = useSetRecoilState(googleMapStyleState);
  const [theme, setTheme] = useRecoilState(googleMapThemeState);
  const [themeName, setThemeName] = useDisplayOperationRecoilState(`${SCREEN_SETTING}.google.theme`, googleMapThemeNameState)

  const changeRoadVisible = useCallback((visible) => {
    const style = makeStyle(visible, roadLabelVisible, landmarkVisible, labelVisible, theme);
    setGoogleMapStyleState(style);
    setRoadVisible(visible);
    // eslint-disable-next-line
  }, [roadLabelVisible, landmarkVisible, labelVisible, theme]);

  const changeRoadLabelVisible = useCallback((visible) => {
    const style = makeStyle(roadVisible, visible, landmarkVisible, labelVisible, theme);
    setGoogleMapStyleState(style);
    setRoadLabelVisible(visible);
    // eslint-disable-next-line
  }, [roadVisible, landmarkVisible, labelVisible, theme]);

  const changeLandmarkVisible = useCallback((visible) => {
    const style = makeStyle(roadVisible, roadLabelVisible, visible, labelVisible, theme);
    setGoogleMapStyleState(style);
    setLandmarkVisible(visible);
    // eslint-disable-next-line
  }, [roadVisible, roadLabelVisible, labelVisible, theme]);

  const changeLabelVisible = useCallback((visible) => {
    const style = makeStyle(roadVisible, roadLabelVisible, landmarkVisible, visible, theme);
    setGoogleMapStyleState(style);
    setLabelVisible(visible);
    // eslint-disable-next-line
  }, [roadVisible, roadLabelVisible, landmarkVisible, theme]);

  const changeTheme = useCallback((theme) => {
    let _theme = getTheme(theme);
    const style = makeStyle(roadVisible, roadLabelVisible, landmarkVisible, labelVisible, _theme);
    setGoogleMapStyleState(style);
    setTheme(_theme);
    setThemeName(theme);
    // eslint-disable-next-line
  }, [roadVisible, roadLabelVisible, landmarkVisible, labelVisible]);

  return {
    roadVisible,
    roadLabelVisible,
    landmarkVisible,
    labelVisible,
    themeName,
    changeRoadVisible,
    changeRoadLabelVisible,
    changeLandmarkVisible,
    changeLabelVisible,
    changeTheme
  }
};